import React, { useMemo, useRef, useState } from "react";
import {
  Calendar3,
  ChevronDown,
  ArrowLeft,
  Check2,
} from "react-bootstrap-icons";
import { useClickOutside } from "../../hooks/useClickOutside";
import "../../styles/DateRangeSelect.css";

const MIN_DATE = "1900-01-01";
const MAX_DATE = "9999-12-31";

/** Clamps the year portion of a YYYY-MM-DD string to 4 digits. */
function sanitizeDate(value) {
  if (!value) return value;
  const dash = value.indexOf("-");
  if (dash > 4) return value.slice(0, 4) + value.slice(dash);
  return value;
}

const PRESETS = [
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "12m", label: "Last 12 months" },
  { value: "ytd", label: "Year to date" },
];

/**
 * Date range selector with preset shortcuts and a custom range picker.
 *
 * Props:
 *  - value      string preset ("3m" | "6m" | "12m" | "ytd")
 *               or { value: "custom", startDate, endDate }
 *  - onChange   (next) => void
 */
export function DateRangeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [draftStart, setDraftStart] = useState("");
  const [draftEnd, setDraftEnd] = useState("");
  const wrapRef = useRef(null);

  const isCustom = typeof value === "object" && value?.value === "custom";

  const closeAll = () => {
    setOpen(false);
    setShowCustom(false);
  };

  useClickOutside([wrapRef], open, closeAll);

  const dateError = useMemo(() => {
    const yearOf = (d) => (d ? parseInt(d.split("-")[0], 10) : null);
    const sy = yearOf(draftStart);
    const ey = yearOf(draftEnd);
    if (draftStart && (isNaN(sy) || sy < 1900))
      return "Start year must be between 1900 and 9999";
    if (draftEnd && (isNaN(ey) || ey < 1900))
      return "End year must be between 1900 and 9999";
    if (draftStart && draftEnd && draftStart > draftEnd)
      return "End date must be on or after start date";
    return null;
  }, [draftStart, draftEnd]);

  const canApply = Boolean(draftStart) && Boolean(draftEnd) && !dateError;

  const activeLabel = useMemo(() => {
    if (isCustom) return formatRange(value.startDate, value.endDate);
    return PRESETS.find((p) => p.value === value)?.label || "Select range";
  }, [value, isCustom]);

  const handlePresetSelect = (presetValue) => {
    onChange(presetValue);
    closeAll();
  };

  const handleEnterCustom = () => {
    if (isCustom) {
      setDraftStart(value.startDate || "");
      setDraftEnd(value.endDate || "");
    }
    setShowCustom(true);
  };

  const handleApplyCustom = () => {
    if (!canApply) return;
    onChange({ value: "custom", startDate: draftStart, endDate: draftEnd });
    closeAll();
  };

  return (
    <div className="cp-dr" ref={wrapRef}>
      <button
        type="button"
        className={"cp-dr__trigger" + (open ? " is-open" : "")}
        onClick={() => (open ? closeAll() : setOpen(true))}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="cp-dr__trigger-icon" aria-hidden="true">
          <Calendar3 />
        </span>
        <span className="cp-dr__trigger-label">{activeLabel}</span>
        <ChevronDown
          className={"cp-dr__trigger-chevron" + (open ? " is-flipped" : "")}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="cp-dr__menu"
          role="dialog"
          aria-label="Select date range"
        >
          {!showCustom ? (
            <PresetMenu
              currentValue={value}
              isCustom={isCustom}
              onSelect={handlePresetSelect}
              onChooseCustom={handleEnterCustom}
            />
          ) : (
            <CustomMenu
              draftStart={draftStart}
              draftEnd={draftEnd}
              onStartChange={(v) => setDraftStart(sanitizeDate(v))}
              onEndChange={(v) => setDraftEnd(sanitizeDate(v))}
              onBack={() => setShowCustom(false)}
              onApply={handleApplyCustom}
              onCancel={closeAll}
              error={dateError}
              canApply={canApply}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PresetMenu({ currentValue, isCustom, onSelect, onChooseCustom }) {
  return (
    <>
      <div className="cp-dr__header">
        <div className="cp-dr__header-eyebrow">Date Range</div>
      </div>

      <div className="cp-dr__body">
        <div className="cp-dr__preset-grid">
          {PRESETS.map((p) => {
            const active = !isCustom && currentValue === p.value;
            return (
              <button
                key={p.value}
                type="button"
                className={"cp-dr__preset" + (active ? " is-active" : "")}
                onClick={() => onSelect(p.value)}
              >
                <span>{p.label}</span>
                {active && (
                  <Check2 className="cp-dr__preset-check" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>

        <div className="cp-dr__divider" role="separator" />

        <button
          type="button"
          className={"cp-dr__custom-link" + (isCustom ? " is-active" : "")}
          onClick={onChooseCustom}
        >
          <span className="cp-dr__custom-link-label">
            <Calendar3 aria-hidden="true" />
            Custom range
          </span>
          <span className="cp-dr__custom-link-arrow" aria-hidden="true">
            ›
          </span>
        </button>
      </div>
    </>
  );
}

function CustomMenu({
  draftStart,
  draftEnd,
  onStartChange,
  onEndChange,
  onBack,
  onApply,
  onCancel,
  error,
  canApply,
}) {
  return (
    <>
      <div className="cp-dr__header cp-dr__header--with-back">
        <button
          type="button"
          className="cp-dr__back"
          onClick={onBack}
          aria-label="Back to presets"
        >
          <ArrowLeft />
        </button>
        <div className="cp-dr__header-eyebrow">Custom Range</div>
      </div>

      <div className="cp-dr__body">
        <div className={"cp-dr__date-panel" + (error ? " has-error" : "")}>
          <div className="cp-dr__date-cell">
            <label className="cp-dr__date-label" htmlFor="cp-dr-start">
              From
            </label>
            <input
              id="cp-dr-start"
              type="date"
              className="cp-dr__date-input"
              value={draftStart}
              min={MIN_DATE}
              max={draftEnd || MAX_DATE}
              onChange={(e) => onStartChange(e.target.value)}
            />
          </div>
          <div className="cp-dr__date-divider" aria-hidden="true" />
          <div className="cp-dr__date-cell">
            <label className="cp-dr__date-label" htmlFor="cp-dr-end">
              To
            </label>
            <input
              id="cp-dr-end"
              type="date"
              className="cp-dr__date-input"
              value={draftEnd}
              min={draftStart || MIN_DATE}
              max={MAX_DATE}
              onChange={(e) => onEndChange(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="cp-dr__error" role="alert">
            {error}
          </div>
        )}

        <div className="cp-dr__footer">
          <button type="button" className="cp-dr__cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="cp-dr__apply"
            onClick={onApply}
            disabled={!canApply}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}

function formatRange(start, end) {
  if (!start || !end) return "Custom range";
  return `${formatShort(start)} – ${formatShort(end)}`;
}

function formatShort(iso) {
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
